import { useEffect, useState, useCallback, useRef } from "react";
import Modal from "shared/Modal";
import { Loader2 } from "lucide-react";
import { AttendanceUpdateRequestItem, fetchAllUpdateRequests, fetchMyUpdateRequests, reviewUpdateRequest } from "../api/attendanceApi";
import { REQUEST_STATUS_STYLES, REQUEST_STATUS_FILTERS } from "./requestStatusStyles";
import ReviewControls from "./ReviewControls";

interface Props {
  role: "admin" | "hr";
  onClose: () => void;
  onReviewed?: () => void;
}

const ACTION_LABELS: Record<string, string> = { add: "Add", edit: "Edit", delete: "Remove" };

export default function UpdateRequestsPanel({ role, onClose, onReviewed }: Props) {
  const [items, setItems] = useState<AttendanceUpdateRequestItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("pending");

  // Tracks any in-flight review across the whole list (each row's own
  // ReviewControls instance) so the panel can refuse to close mid-request —
  // matches AttendanceActionModal/LeaveRequestModal, which both guard their
  // own close the same way while submitting. Ref-counted (not a single
  // boolean) since multiple rows can be busy at once.
  const activeOpsRef = useRef(0);
  const [hasActiveOps, setHasActiveOps] = useState(false);
  const beginOp = useCallback(() => {
    activeOpsRef.current += 1;
    setHasActiveOps(true);
  }, []);
  const endOp = useCallback(() => {
    activeOpsRef.current = Math.max(0, activeOpsRef.current - 1);
    if (activeOpsRef.current === 0) setHasActiveOps(false);
  }, []);

  // Concurrent reviews on different rows each trigger their own load() — if
  // their responses arrive out of order, an older one resolving after a
  // newer one would otherwise overwrite the list with stale data. Only the
  // most-recently-*started* load's response is ever applied.
  const loadSeqRef = useRef(0);
  const load = useCallback(async () => {
    const seq = ++loadSeqRef.current;
    setLoading(true);
    try {
      const data = role === "admin"
        ? await fetchAllUpdateRequests(statusFilter || undefined)
        : await fetchMyUpdateRequests(statusFilter || undefined);
      if (seq === loadSeqRef.current) setItems(data.items || []);
    } catch (err) {
      console.error("Failed to load update requests:", err);
    }
    if (seq === loadSeqRef.current) setLoading(false);
  }, [role, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReview = async (id: number, decision: "approve" | "reject", note?: string) => {
    beginOp();
    try {
      await reviewUpdateRequest(id, decision, note);
      await load();
      onReviewed?.();
    } catch (err: any) {
      alert(err?.response?.data?.detail || "Failed to review request.");
    }
    endOp();
  };

  return (
    <Modal size="lg" onClick={hasActiveOps ? undefined : onClose} closeButtonCLick={hasActiveOps ? undefined : onClose} modalClassName="p-6">
      <h2 className="text-lg font-semibold text-foreground font-inter mb-4">
        {role === "admin" ? "Attendance Update Requests" : "My Attendance Update Requests"}
      </h2>

      <div className="flex gap-2 mb-4">
        {REQUEST_STATUS_FILTERS.map((s) => (
          <button
            key={s || "all"}
            onClick={() => setStatusFilter(s)}
            className={`px-3 py-1.5 text-xs font-inter rounded-lg transition-colors ${
              statusFilter === s ? "bg-primary text-primary-foreground" : "bg-surface-muted text-muted-foreground hover:text-foreground"
            }`}
          >
            {s ? s[0].toUpperCase() + s.slice(1) : "All"}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-10">
          <Loader2 className="w-5 h-5 text-primary animate-spin" />
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-muted-foreground font-inter text-center py-10">No requests found.</p>
      ) : (
        <div className="space-y-3">
          {items.map((r) => (
            <div key={r.id} className="border border-border rounded-xl p-3">
              <div className="flex items-start justify-between gap-3">
                <div>
                  <p className="text-sm font-medium text-foreground font-inter">
                    {ACTION_LABELS[r.action]} · {r.employee_code} · {r.attendance_date}
                  </p>
                  {r.requested_timestamp && (
                    <p className="text-xs text-muted-foreground font-inter mt-0.5">
                      {r.requested_status === 0 ? "Check In" : "Check Out"} at {r.requested_timestamp.slice(11, 16)}
                    </p>
                  )}
                  <p className="text-xs text-muted-foreground font-inter mt-1">"{r.reason}"</p>
                  {role === "admin" && (
                    <p className="text-xs text-muted-foreground font-inter mt-1">Requested by {r.requested_by}</p>
                  )}
                  {r.review_note && (
                    <p className="text-xs text-muted-foreground font-inter mt-1">Review note: {r.review_note}</p>
                  )}
                </div>
                <span
                  className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium font-inter whitespace-nowrap ${REQUEST_STATUS_STYLES[r.status]}`}
                >
                  {r.status}
                </span>
              </div>

              {role === "admin" && r.status === "pending" && (
                <div className="mt-3">
                  <ReviewControls
                    onApprove={(note) => handleReview(r.id, "approve", note)}
                    onReject={(note) => handleReview(r.id, "reject", note)}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
}
